<template>
  <CContainer fluid class="grid-stripped">
    <!-- Email Type Selection -->
    <PFRow :label="$pgettext('email_templates', 'Email Type')">
      <PSelectKeyValue
        v-if="!edit"
        :options="emailTypeOptions"
        :value.sync="form.email_type"
        label="label"
        track-by="value"/>
      <CInput
        v-else
        :value="getEmailTypeLabel(form.email_type)"
        disabled/>
      <small class="text-muted">
        <translate translate-context="email_templates">
          Select the type of transactional email
        </translate>
      </small>
    </PFRow>

    <!-- Delivery Service -->
    <PFRow :label="$pgettext('email_templates', 'Delivery Service')">
      <PSelectKeyValue
        :options="deliveryServiceOptions"
        :value.sync="form.delivery_service"
        label="label"
        track-by="value"/>
      <small class="text-muted">
        <translate translate-context="email_templates">
          Select the email service provider for sending this email type
        </translate>
      </small>
    </PFRow>

    <!-- Enabled -->
    <PFRow :label="$pgettext('email_templates', 'Status')">
      <div class="d-flex align-items-center">
        <CSwitch
          color="primary"
          variant="3d"
          size="sm"
          :checked.sync="form.enabled"/>
        <label class="ml-2 mb-0">
          <translate translate-context="email_templates">Enable this template</translate>
        </label>
      </div>
      <small class="text-muted">
        <translate translate-context="email_templates">
          When enabled, this template configuration will be used for sending emails
        </translate>
      </small>
    </PFRow>

    <!-- Debug Mode -->
    <PFRow :label="$pgettext('email_templates', 'Debug Mode')">
      <PSelectKeyValue
        :options="debugModeOptions"
        :value.sync="form.debug_mode"
        label="label"
        track-by="value"/>
      <small class="text-muted">
        <translate translate-context="email_templates">
          Controls email logging and sending behavior. "Off" sends normally, "Log Only" logs without sending, "Log &amp; Send" logs and sends.
        </translate>
      </small>
    </PFRow>

    <!-- Sender Information -->
    <hr class="my-4">
    <h5><translate translate-context="email_templates">Sender Information</translate></h5>

    <PFRow :label="$pgettext('email_templates', 'Sender Address')">
      <CInput
        v-model="form.sender_address"
        type="email"
        :placeholder="$pgettext('email_templates', 'noreply@example.com')"/>
      <small class="text-muted">
        <translate translate-context="email_templates">
          The email address that will appear as the sender (optional, uses service default if empty)
        </translate>
      </small>
    </PFRow>

    <PFRow :label="$pgettext('email_templates', 'Sender Name')">
      <CInput
        v-model="form.sender_name"
        type="text"
        :placeholder="$pgettext('email_templates', 'Sender Name')"/>
      <small class="text-muted">
        <translate translate-context="email_templates">
          The name that will appear as the sender (optional)
        </translate>
      </small>
    </PFRow>

    <!-- BCC Configuration -->
    <hr class="my-4">
    <h5><translate translate-context="email_templates">BCC Configuration</translate></h5>

    <PFRow :label="$pgettext('email_templates', 'BCC Enabled')">
      <div class="d-flex align-items-center">
        <CSwitch
          color="primary"
          variant="3d"
          size="sm"
          :checked.sync="form.bcc_enabled"/>
        <label class="ml-2 mb-0">
          <translate translate-context="email_templates">Send blind carbon copies</translate>
        </label>
      </div>
      <small class="text-muted">
        <translate translate-context="email_templates">
          When enabled, a copy of all emails will be sent to the specified addresses
        </translate>
      </small>
    </PFRow>

    <PFRow v-if="form.bcc_enabled" :label="$pgettext('email_templates', 'BCC Addresses')">
      <PEmailTagInput
        :value.sync="form.bcc_addresses"
        :placeholder="$pgettext('email_templates', 'email@example.com')"/>
      <small class="text-muted">
        <translate translate-context="email_templates">
          Type an email address and press Enter to add it to the list. You can paste multiple emails separated by commas.
        </translate>
      </small>
    </PFRow>

    <!-- Template IDs (per language) -->
    <hr class="my-4">
    <h5><translate translate-context="email_templates">Template Configuration</translate></h5>

    <PFRow :label="$pgettext('email_templates', 'Template ID')">
      <PTranslatableTextInput
        :value="form.template"
        @input="form.template = $event"
        :placeholder="$pgettext('email_templates', 'Enter template ID')"/>
      <small class="text-muted">
        <translate translate-context="email_templates">
          The template ID in your email service provider. Configure for each language if needed.
        </translate>
      </small>
    </PFRow>

    <!-- Custom Settings (if defined for this email type) -->
    <div v-if="emailTypeSettings && emailTypeSettings.length > 0">
      <hr class="my-4">
      <h5><translate translate-context="email_templates">Additional Settings</translate></h5>

      <PFRow v-for="setting in emailTypeSettings" :key="setting.key" :label="setting.name">
        <!-- Number input -->
        <CInput
          v-if="setting.type === 'number'"
          type="number"
          v-model.number="form.settings[setting.key]"
          :placeholder="setting.default ? String(setting.default) : ''"/>

        <!-- Text input -->
        <CInput
          v-else-if="setting.type === 'text'"
          type="text"
          v-model="form.settings[setting.key]"
          :placeholder="setting.default || ''"/>

        <!-- Boolean switch -->
        <div v-else-if="setting.type === 'boolean'" class="d-flex align-items-center">
          <CSwitch
            color="primary"
            variant="3d"
            size="sm"
            :checked.sync="form.settings[setting.key]"/>
          <label class="ml-2 mb-0">{{ setting.name }}</label>
        </div>

        <!-- Select dropdown -->
        <PSelectKeyValue
          v-else-if="setting.type === 'select'"
          :options="setting.options"
          :value.sync="form.settings[setting.key]"
          label="label"
          track-by="value"/>

        <small v-if="setting.hint" class="text-muted">{{ setting.hint }}</small>
      </PFRow>
    </div>

    <!-- Translations (custom key-value) -->
    <hr class="my-4">
    <h5><translate translate-context="email_templates">Custom Translations</translate></h5>
    <p class="text-muted">
      <translate translate-context="email_templates">
        Define custom translation keys that can be used as parameters in your email templates.
      </translate>
    </p>
    <CAlert v-if="availableParameterNames.length > 0" color="info" class="mb-3">
      <small>
        <CIcon name="cilInfo" class="mr-1"/>
        <translate translate-context="email_templates">
          You can use placeholders in your translations with the syntax {parameter_name}.
          Only parameters listed in "Available Template Parameters" below are valid.
        </translate>
      </small>
    </CAlert>

    <DynamicTranslationsEditor
      :translations="form.translations"
      :available-parameters="availableParameterNames"
      @update="form.translations = $event"/>

    <!-- Available Parameters Section -->
    <hr class="my-4">
    <div v-if="hasParametersOrTranslations" class="parameters-section">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="parameters-section-title mb-0">
          <CIcon name="cil-info" class="mr-2"/>
          <translate translate-context="email_templates">Available Template Parameters</translate>
        </h5>
        <!-- Language Selector for Preview -->
        <div v-if="translationKeys.length > 0" class="d-flex align-items-center">
          <small class="text-muted mr-2">
            <translate translate-context="email_templates">Preview language:</translate>
          </small>
          <CButtonGroup size="sm">
            <CButton
              v-for="langOpt in previewLangOptions"
              :key="langOpt.value"
              :color="selectedPreviewLang === langOpt.value ? 'primary' : 'secondary'"
              :variant="selectedPreviewLang === langOpt.value ? '' : 'outline'"
              @click="previewLang = langOpt.value"
            >
              {{ langOpt.label }}
            </CButton>
          </CButtonGroup>
        </div>
      </div>
      <p class="mb-3 text-muted">
        <translate translate-context="email_templates">
          These parameters will be automatically injected into your email template.
        </translate>
        <span v-if="form.delivery_service">
          <translate translate-context="email_templates">
            Use the syntax shown below based on your selected delivery service.
          </translate>
        </span>
      </p>

      <!-- Dynamic Parameters Display Based on Provider -->
      <MailUpParametersDisplay
        v-if="templateType === 'mailup'"
        :available-parameters="currentAvailableParameters"
        :translation-keys="translationKeys">
        <template #translation-preview="{ translationKey }">
          <span v-if="getTranslationPreviewHtml(translationKey)" class="translation-preview-value" v-html="getTranslationPreviewHtml(translationKey)"></span>
          <span v-else class="text-warning">
            {{ noTranslationText }}
          </span>
        </template>
      </MailUpParametersDisplay>

      <BrevoParametersDisplay
        v-else-if="templateType === 'brevo'"
        :available-parameters="currentAvailableParameters"
        :translation-keys="translationKeys">
        <template #translation-preview="{ translationKey }">
          <span v-if="getTranslationPreviewHtml(translationKey)" class="translation-preview-value" v-html="getTranslationPreviewHtml(translationKey)"></span>
          <span v-else class="text-warning">
            {{ noTranslationText }}
          </span>
        </template>
      </BrevoParametersDisplay>

      <DefaultParametersDisplay
        v-else
        :available-parameters="currentAvailableParameters"
        :translation-keys="translationKeys">
        <template #translation-preview="{ translationKey }">
          <span v-if="getTranslationPreviewHtml(translationKey)" class="translation-preview-value" v-html="getTranslationPreviewHtml(translationKey)"></span>
          <span v-else class="text-warning">
            {{ noTranslationText }}
          </span>
        </template>
      </DefaultParametersDisplay>

      <!-- Template IDs Summary -->
      <div v-if="hasTemplateIds" class="template-ids-summary mt-4">
        <strong class="d-block mb-2">
          <translate translate-context="email_templates">Configured Template IDs:</translate>
        </strong>
        <div class="template-ids-list">
          <CBadge
            v-for="(templateId, lang) in form.template"
            :key="lang"
            color="secondary"
            class="mr-2 mb-2">
            <span class="text-uppercase font-weight-bold">{{ lang }}</span>: <code class="ml-1">{{ templateId }}</code>
          </CBadge>
        </div>
      </div>
    </div>
  </CContainer>
</template>

<script>
import PFRow from '@/domain/core/components/PFRow.vue'
import PSelectKeyValue from '@/domain/core/components/PSelectKeyValue.vue'
import PTranslatableTextInput from '@/domain/core/components/PTranslatableTextInput.vue'
import PEmailTagInput from '@/domain/core/components/PEmailTagInput.vue'
import DynamicTranslationsEditor from '@/domain/eventWizard/components/emailTypeConfig/DynamicTranslationsEditor.vue'
import MailUpParametersDisplay from './MailUpParametersDisplay.vue'
import BrevoParametersDisplay from './BrevoParametersDisplay.vue'
import DefaultParametersDisplay from './DefaultParametersDisplay.vue'
import { getDeliveryServiceOptions } from '@/domain/core/constant/deliveryServices'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'EmailTemplateForm',
  components: {
    PFRow,
    PSelectKeyValue,
    PTranslatableTextInput,
    PEmailTagInput,
    DynamicTranslationsEditor,
    MailUpParametersDisplay,
    BrevoParametersDisplay,
    DefaultParametersDisplay
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    edit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['save'],
  data() {
    return {
      form: {
        email_type: '',
        delivery_service: 'MailUpAgent',
        enabled: true,
        debug_mode: 'off',
        bcc_enabled: false,
        bcc_addresses: [],
        sender_address: '',
        sender_name: '',
        template: {},
        translations: {},
        settings: {}
      },
      previewLang: null // Selected language for translation preview
    }
  },
  computed: {
    ...mapGetters('login', ['userLanguage']),
    ...mapGetters('emailTemplates', ['availableTypes', 'availableTypeByValue', 'availableParameters', 'availableSettings']),

    /**
     * Extract template provider type from delivery_service
     * @returns {'mailup'|'brevo'|'ses'|'default'}
     */
    templateType() {
      const service = this.form.delivery_service
      if (!service) return 'default'

      if (service.includes('MailUp')) return 'mailup'
      if (service === 'BrevoAgent') return 'brevo'
      if (service === 'DjangoDebugAgent') return 'ses'

      return 'default'
    },

    noTranslationText() {
      return this.$pgettext('email_templates', 'No translation value set')
    },
    emailTypeOptions() {
      return this.availableTypes.map(type => ({
        value: type.value,
        label: type.label
      }))
    },
    deliveryServiceOptions() {
      return getDeliveryServiceOptions()
    },
    debugModeOptions() {
      return [
        {
          value: 'off',
          label: this.$pgettext('email_templates', 'Off - Normal sending')
        },
        {
          value: 'log_only',
          label: this.$pgettext('email_templates', 'Log Only - Log without sending')
        },
        {
          value: 'log_and_send',
          label: this.$pgettext('email_templates', 'Log & Send - Log and send')
        }
      ]
    },
    emailTypeSettings() {
      if (!this.form.email_type) return []
      // Get settings from API parameters endpoint
      const settings = this.availableSettings(this.form.email_type)

      // Convert API format to form format
      // API: {name, type, description, required, default}
      // Form expects: {key, name, hint, type, default}
      return settings.map(setting => ({
        key: setting.name,
        name: setting.name,
        hint: setting.description,
        type: setting.type === 'integer' ? 'number' : setting.type,
        default: setting.default
      }))
    },
    currentAvailableParameters() {
      if (!this.form.email_type) return {}
      const params = this.availableParameters(this.form.email_type)
      // Convert API format to the format expected by display components
      // API returns: { parameters: [{name, type, description, required, schema}] }
      // Display expects: { param_name: description, array[]: description, array[].field: description }
      if (params && params.parameters && Array.isArray(params.parameters)) {
        const result = {}

        params.parameters.forEach(param => {
          // Add the parameter itself
          result[param.name] = param.description || param.name

          // If it's a list/array with schema, expand the schema fields
          if ((param.type === 'list' || param.type === 'dict') && param.schema) {
            // Add array marker
            result[param.name + '[]'] = param.description || param.name

            // Add each field in the schema
            Object.entries(param.schema).forEach(([fieldName, fieldDef]) => {
              const fullKey = param.name + '[].' + fieldName
              result[fullKey] = fieldDef.description || fieldName
            })
          }
        })

        return result
      }
      return params
    },
    translationKeys() {
      const keys = new Set()
      if (this.form.translations && typeof this.form.translations === 'object') {
        Object.values(this.form.translations).forEach(langTranslations => {
          if (typeof langTranslations === 'string') {
            try {
              langTranslations = JSON.parse(langTranslations)
            } catch (e) {
              return
            }
          }
          if (langTranslations && typeof langTranslations === 'object') {
            Object.keys(langTranslations).forEach(key => keys.add(key))
          }
        })
      }
      return Array.from(keys).sort()
    },
    hasParametersOrTranslations() {
      return Object.keys(this.currentAvailableParameters).length > 0 || this.translationKeys.length > 0
    },
    /**
     * Extract parameter names from currentAvailableParameters for placeholder validation
     * @returns {Array<string>}
     */
    availableParameterNames() {
      return Object.keys(this.currentAvailableParameters)
    },
    hasTemplateIds() {
      return this.form.template &&
             typeof this.form.template === 'object' &&
             Object.values(this.form.template).some(v => v)
    },
    /**
     * Language options for preview selector
     */
    previewLangOptions() {
      const langs = process.env.VUE_APP_SUPPORTED_LANGS.split(',')
      return langs.map(lang => ({
        value: lang,
        label: lang.toUpperCase()
      }))
    },
    /**
     * Currently selected preview language (defaults to user language)
     */
    selectedPreviewLang() {
      return this.previewLang || this.userLanguage || 'en'
    }
  },
  async mounted() {
    this.onReset()
    // Load available parameters if email type is already set
    if (this.form.email_type) {
      await this.loadAvailableParameters()
    }
  },
  methods: {
    ...mapActions('emailTemplates', ['fetchAvailableParameters']),

    async loadAvailableParameters() {
      if (!this.form.email_type) return
      try {
        await this.fetchAvailableParameters(this.form.email_type)
      } catch (error) {
        console.error('Failed to load available parameters:', error)
      }
    },

    getEmailTypeLabel(emailType) {
      const emailTypeConfig = this.availableTypeByValue(emailType)
      return emailTypeConfig ? emailTypeConfig.label : emailType
    },
    onReset() {
      // Deep clone the data to avoid reactivity issues
      this.form = {
        email_type: this.data.email_type || '',
        delivery_service: this.data.delivery_service || 'MailUpAgent',
        enabled: this.data.enabled !== undefined ? this.data.enabled : true,
        debug_mode: this.data.debug_mode || 'off',
        bcc_enabled: this.data.bcc_enabled !== undefined ? this.data.bcc_enabled : false,
        bcc_addresses: Array.isArray(this.data.bcc_addresses)
          ? [...this.data.bcc_addresses]
          : (this.data.bcc_addresses || []),
        sender_address: this.data.sender_address || '',
        sender_name: this.data.sender_name || '',
        template: this.data.template ? { ...this.data.template } : {},
        translations: {},
        settings: this.data.settings ? { ...this.data.settings } : {}
      }

      // Parse translations from JSON strings to objects
      if (this.data.translations) {
        const parsedTranslations = {}
        Object.keys(this.data.translations).forEach(lang => {
          if (typeof this.data.translations[lang] === 'string') {
            try {
              // Replace single quotes with double quotes for valid JSON
              const jsonString = this.data.translations[lang].replace(/'/g, '"')
              parsedTranslations[lang] = JSON.parse(jsonString)
            } catch (e) {
              console.error(`Failed to parse translations for ${lang}:`, this.data.translations[lang], e)
              parsedTranslations[lang] = {}
            }
          } else {
            parsedTranslations[lang] = this.data.translations[lang] ? { ...this.data.translations[lang] } : {}
          }
        })
        this.form.translations = parsedTranslations
      }

      this.initializeSettings()
    },
    initializeSettings() {
      // Initialize settings with default values from email type definition
      this.emailTypeSettings.forEach(setting => {
        if (this.form.settings[setting.key] === undefined) {
          this.$set(this.form.settings, setting.key, setting.default)
        }
      })
    },
    getTranslationPreview(key) {
      if (!this.form.translations || typeof this.form.translations !== 'object') {
        return null
      }

      let translations = this.form.translations[this.selectedPreviewLang]
      if (typeof translations === 'string') {
        try {
          translations = JSON.parse(translations)
        } catch (e) {
          return null
        }
      }

      if (translations && typeof translations === 'object' && translations[key]) {
        return {
          value: translations[key],
          lang: this.selectedPreviewLang
        }
      }

      return null
    },
    /**
     * Generate HTML preview with badges for placeholders
     * @param {string} key - Translation key
     * @returns {string} - HTML string with badges
     */
    getTranslationPreviewHtml(key) {
      const preview = this.getTranslationPreview(key)
      if (!preview || !preview.value) return null

      const text = preview.value
      // Escape HTML first
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      // Replace placeholders with badges
      const html = escaped.replace(/\{([^}]+)\}/g, (match, placeholder) => {
        const isValid = this.availableParameterNames.includes(placeholder)
        const badgeClass = isValid ? 'badge-success' : 'badge-danger'
        return `<span class="badge ${badgeClass} placeholder-badge">${placeholder}</span>`
      })

      return html
    },
    async onSave() {
      this.$emit('save', this.form)
    }
  },
  watch: {
    'form.email_type': {
      async handler(newValue, oldValue) {
        this.initializeSettings()
        // Load parameters when email type changes
        if (newValue && newValue !== oldValue) {
          await this.loadAvailableParameters()
        }
      }
    }
  }
}
</script>

<style scoped>
.grid-stripped {
  padding: 0;
}

hr {
  border-top: 1px solid #dee2e6;
}

h5 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
}

/* Parameters Section */
.parameters-section {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1.5rem;
}

.parameters-section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.template-ids-summary {
  background: white;
  padding: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
}

/* Translation Preview with Placeholder Badges */
.translation-preview-value {
  font-size: 0.9em;
  line-height: 1.5;
}

.translation-preview-value >>> .placeholder-badge {
  font-size: 0.8em;
  padding: 0.15em 0.4em;
  margin: 0 0.15em;
  vertical-align: baseline;
  font-weight: 500;
  border-radius: 0.25rem;
}

.translation-preview-value >>> .badge-success {
  background-color: #2eb85c;
  color: white;
}

.translation-preview-value >>> .badge-danger {
  background-color: #e55353;
  color: white;
}

.template-ids-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
</style>
